import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI, paymentAPI, nasAPI, settingsAPI, connectionRequestAPI, customerAPI, gtplSaathiAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Users, Tv, CreditCard, RefreshCw, MessageSquare,
  TrendingUp, Clock, Activity, UserPlus, Plus,
  ChevronDown, ChevronUp, Banknote, IndianRupee, Receipt,
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import toast from 'react-hot-toast';
import useDarkReady from '../theme/useDarkReady';
import { useViewMode } from '../hooks/useViewMode';
import AppDashboard from './AppDashboard';
import AssistantFab from '../components/assistant/AssistantFab';
import { IS_DEMO, HIDE_GTPL } from '../utils/demo';

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n));

// Local (not UTC) yyyy-mm-dd — matches the <input type="date"> convention already used on
// PaymentsPage, so the same string round-trips through the /payments startDate/endDate filters
// without shifting a day at IST midnight the way toISOString() would.
const localDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const fmtMbps = (v) => (v == null ? '—' : v >= 100 ? v.toFixed(0) : v >= 10 ? v.toFixed(1) : v.toFixed(2));

function ifaceDisplayName(n) {
  return n.replace(/^AHM\s*-\s*MATRIX\s*TO\s+/i, '').substring(0, 40);
}

// NAS Bandwidth — opt-in widget (Settings → App View → Dashboard Widgets), one panel
// per active NAS poller.
function NASBandwidthPanel({ nasId, nasName }) {
  const [bw, setBw]         = useState({ configured: false, warming: true, data: [], current: null });
  const [ifaces, setIfaces] = useState([]);
  const [iface, setIface]   = useState('');
  const [localHist, setLocalHist] = useState([]);
  const [warmPollCount, setWarmPollCount] = useState(0);
  const ifaceRef  = useRef('');
  const timerRef  = useRef(null);

  const dropdownSt = {
    fontSize: '.78rem', padding: '3px 8px', borderRadius: 5,
    border: '1px solid var(--border)', background: 'var(--card-bg, var(--cb-ffffff))', color: 'var(--text)', cursor: 'pointer',
  };

  const fetchBw = () => {
    const sel = ifaceRef.current;
    nasAPI.getBandwidth(nasId, sel || null)
      .then(r => {
        if (!sel) {
          setBw(r.data);
          if (r.data.warming) {
            setWarmPollCount(c => c + 1);
          } else {
            setWarmPollCount(0);
          }
        } else {
          const { rx = 0, tx = 0 } = r.data.data || {};
          const time = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
          setLocalHist(h => { const n = [...h, { time, rx: +rx.toFixed(2), tx: +tx.toFixed(2) }]; return n.length > 60 ? n.slice(-60) : n; });
        }
      }).catch(() => {});
  };

  useEffect(() => {
    nasAPI.getInterfaces(nasId).then(r => setIfaces(r.data.data || [])).catch(() => {});
    fetchBw();
    timerRef.current = setInterval(fetchBw, 1_000);
    return () => clearInterval(timerRef.current);
  }, [nasId]); // eslint-disable-line

  const usingIface = !!iface;
  const graphData  = usingIface ? localHist : bw.data;
  const cur        = usingIface ? (localHist.length > 0 ? localHist[localHist.length - 1] : null) : bw.current;
  const cap        = bw.current?.totalCapMbps || null;
  const onlineCount = bw.current?.onlineCount ?? null;
  const rxPct = cap && cur ? Math.min(100, (cur.rx / cap) * 100) : null;
  const txPct = cap && cur ? Math.min(100, (cur.tx / cap) * 100) : null;

  return (
    <div className="card mb-6">
      <div className="card-header">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <Activity size={15} color="var(--primary)" />
          {nasName}
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {ifaces.length > 0 && (
            <select value={iface} onChange={e => { setIface(e.target.value); ifaceRef.current = e.target.value; setLocalHist([]); }} style={{ ...dropdownSt, maxWidth: 220 }}>
              <option value="">All WAN</option>
              {ifaces.map(n => <option key={n} value={n}>{ifaceDisplayName(n)}</option>)}
            </select>
          )}
          {bw.configured && !bw.warming && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '.73rem', color: 'var(--text-muted)' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--cb-22c55e)', display: 'inline-block', animation: 'livePulse 1.8s ease-out infinite' }} />
              LIVE · 1s
            </span>
          )}
        </div>
      </div>

      {bw.warming && warmPollCount < 10 ? (
        <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-muted)', fontSize: '.85rem' }}>
          <Activity size={24} style={{ opacity: .3, marginBottom: 6 }} />
          <div>Connecting to NAS…</div>
          <div style={{ fontSize: '.75rem', marginTop: 4 }}>Collecting first data samples</div>
        </div>
      ) : (bw.warming && warmPollCount >= 10) || !bw.configured ? (
        <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-muted)', fontSize: '.85rem' }}>
          <Activity size={24} style={{ opacity: .3, marginBottom: 6 }} />
          <div>NAS unreachable — cannot collect bandwidth data</div>
          <div style={{ fontSize: '.75rem', marginTop: 4 }}>
            Check IP / credentials in Settings → NAS Devices.
            Mikrotik requires RouterOS 7.1+ with REST API enabled.
          </div>
        </div>
      ) : (
        <>
          {cur && (
            <div style={{ display: 'flex', flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
              <div style={{ flex: '1 1 160px', padding: '16px 22px', borderRight: '1px solid var(--border)' }}>
                <div style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>↓ Download</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                  <span style={{ fontSize: 'clamp(1.5rem, 6vw, 2.2rem)', fontWeight: 800, color: 'var(--primary)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{fmtMbps(cur.rx)}</span>
                  <span style={{ fontSize: '.85rem', color: 'var(--text-muted)' }}>Mbps</span>
                </div>
                {rxPct !== null && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', marginBottom: 2 }}>{rxPct.toFixed(1)}% of {cap} Mbps</div>
                    <div style={{ height: 4, borderRadius: 2, background: 'var(--cb-e2e8f0)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${rxPct}%`, background: rxPct > 85 ? 'var(--cb-ef4444)' : rxPct > 65 ? 'var(--cb-f97316)' : 'var(--primary)', borderRadius: 2, transition: 'width .3s' }} />
                    </div>
                  </div>
                )}
              </div>
              <div style={{ flex: '1 1 160px', padding: '16px 22px', borderRight: onlineCount !== null && !usingIface ? '1px solid var(--border)' : 'none' }}>
                <div style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--c-ea580c)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>↑ Upload</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                  <span style={{ fontSize: 'clamp(1.5rem, 6vw, 2.2rem)', fontWeight: 800, color: 'var(--c-ea580c)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{fmtMbps(cur.tx)}</span>
                  <span style={{ fontSize: '.85rem', color: 'var(--text-muted)' }}>Mbps</span>
                </div>
                {txPct !== null && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', marginBottom: 2 }}>{txPct.toFixed(1)}% of {cap} Mbps</div>
                    <div style={{ height: 4, borderRadius: 2, background: 'var(--cb-e2e8f0)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${txPct}%`, background: txPct > 85 ? 'var(--cb-ef4444)' : txPct > 65 ? 'var(--cb-f97316)' : 'var(--cb-ea580c)', borderRadius: 2, transition: 'width .3s' }} />
                    </div>
                  </div>
                )}
              </div>
              {!usingIface && onlineCount !== null && (
                <div style={{ flex: '1 1 160px', padding: '16px 22px' }}>
                  <div style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--c-059669)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>Online Users</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                    <span style={{ fontSize: '2.2rem', fontWeight: 800, color: 'var(--c-059669)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{onlineCount}</span>
                    <span style={{ fontSize: '.85rem', color: 'var(--text-muted)' }}>PPPoE</span>
                  </div>
                </div>
              )}
            </div>
          )}
          {graphData.length > 1 ? (
            <div style={{ padding: '10px 4px 4px' }}>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={graphData} margin={{ top: 4, right: 14, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id={`rxG${nasId}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.2} /><stop offset="95%" stopColor="var(--primary)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id={`txG${nasId}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--cl-ea580c)" stopOpacity={0.2} /><stop offset="95%" stopColor="var(--cl-ea580c)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--cl-f1f5f9)" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${v}M`} width={44} />
                  <Tooltip contentStyle={{ fontSize: '.78rem', borderRadius: 6 }} formatter={(v, n) => [`${v} Mbps`, n === 'rx' ? '↓ Download' : '↑ Upload']} />
                  <Legend formatter={n => n === 'rx' ? '↓ Download' : '↑ Upload'} wrapperStyle={{ fontSize: '.78rem', paddingTop: 6 }} />
                  <Area type="monotone" dataKey="rx" stroke="var(--primary)" strokeWidth={2} fill={`url(#rxG${nasId})`} dot={false} activeDot={{ r: 3 }} isAnimationActive={false} />
                  <Area type="monotone" dataKey="tx" stroke="var(--cl-ea580c)" strokeWidth={2} fill={`url(#txG${nasId})`} dot={false} activeDot={{ r: 3 }} isAnimationActive={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: '.85rem' }}>Building graph… one moment.</div>
          )}
        </>
      )}
    </div>
  );
}

// ── Fixed-length ring buffer for the top-of-dashboard live graph: BUF_LEN slots exist
// from the very first render (filled with nulls), and every tick shifts one out on the
// left and pushes one in on the right. The array length — and therefore each point's
// x-position, since recharts' default category axis divides the chart width by however
// many entries currently exist — never changes, so the line doesn't visibly "compress"
// as data arrives, and the rightmost point is always "now". A naive `[...h, next]`
// growing from empty instead re-divides the same width by 1, then 2, then 3... on every
// tick, which is what made the graph look like it was congesting/rescaling each second.
const BUF_LEN = 60;
const emptyBuffer = () => Array.from({ length: BUF_LEN }, () => ({ time: '', rx: null, tx: null }));

// ── NAS Uplink Bandwidth — the Ellora↔CTPL backhaul specifically (NAS 1's Ellora P2P
// link and NAS 2/CTPL's own AIS_BB_2 reading of that same link — see
// nasUplinkService.js for why these aren't just summed, and why NAS 1's two other
// links, Hirawadi and Odhav, are unrelated sites and don't belong here). Uses a fixed-
// length ring buffer (BUF_LEN) so the graph is anchored to "now" on the right edge and
// scrolls left, instead of visibly compressing/rescaling every tick.
function NASUplinkBandwidthPanel() {
  const [bw, setBw] = useState({ configured: false, warming: true, current: null, onlineCount: null, blockedCount: null });
  const [warmPollCount, setWarmPollCount] = useState(0);
  const [history, setHistory] = useState(emptyBuffer);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let mounted = true;
    const fetchBw = () => {
      nasAPI.getUplinkBandwidth().then(r => {
        if (!mounted) return;
        setBw(r.data);
        setWarmPollCount(c => r.data.warming ? c + 1 : 0);
        if (!r.data.warming && r.data.current) {
          const { rx, tx } = r.data.current;
          const time = new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
          setHistory(h => [...h.slice(1), { time, rx, tx }]);
        }
      }).catch(() => {});
    };
    fetchBw();
    const t = setInterval(fetchBw, 1_000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const cur = bw.current;

  if (!bw.configured && warmPollCount < 3) {
    return (
      <div className="card mb-6" style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: '.85rem' }}>
        <Activity size={24} style={{ opacity: .3, marginBottom: 6 }} />
        <div>Connecting to NAS uplink…</div>
      </div>
    );
  }
  if (!bw.configured) {
    return (
      <div className="card mb-6" style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: '.85rem' }}>
        <Activity size={24} style={{ opacity: .3, marginBottom: 6 }} />
        <div>NAS 1 (Ellora) or NAS 2 (AIS_BB_2) not reporting yet.</div>
        <div style={{ fontSize: '.75rem', marginTop: 4 }}>Check Settings → NAS Devices.</div>
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <div
        className="card-header"
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen(o => !o)}
      >
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <Activity size={15} color="var(--primary)" />
          Bandwidth Flow
        </h3>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {!bw.warming && (
            <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '.73rem', color: 'var(--text-muted)' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--cb-22c55e)', display: 'inline-block', animation: 'livePulse 1.8s ease-out infinite' }} />
              LIVE · 1s
            </span>
          )}
          {open ? <ChevronUp size={16} color="var(--text-muted)" /> : <ChevronDown size={16} color="var(--text-muted)" />}
        </span>
      </div>

      {open && (bw.warming ? (
        <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-muted)', fontSize: '.85rem' }}>
          <Activity size={24} style={{ opacity: .3, marginBottom: 6 }} />
          <div>Collecting first data samples…</div>
        </div>
      ) : (
        <>
          {cur && (
            <div style={{ display: 'flex', flexWrap: 'nowrap', borderBottom: '1px solid var(--border)' }}>
              <div style={{ flex: '1 1 0', minWidth: 0, padding: '14px 12px', borderRight: '1px solid var(--border)' }}>
                <div style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>↓ Download</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                  <span style={{ fontSize: 'clamp(1.5rem, 6vw, 2.2rem)', fontWeight: 800, color: 'var(--primary)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{fmtMbps(cur.rx)}</span>
                  <span style={{ fontSize: '.85rem', color: 'var(--text-muted)' }}>Mbps</span>
                </div>
              </div>
              <div style={{ flex: '1 1 0', minWidth: 0, padding: '14px 12px', borderRight: '1px solid var(--border)' }}>
                <div style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--c-ea580c)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>↑ Upload</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                  <span style={{ fontSize: 'clamp(1.5rem, 6vw, 2.2rem)', fontWeight: 800, color: 'var(--c-ea580c)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>{fmtMbps(cur.tx)}</span>
                  <span style={{ fontSize: '.85rem', color: 'var(--text-muted)' }}>Mbps</span>
                </div>
              </div>
              <Link to="/nas/online-subscribers" style={{ flex: '1 1 0', minWidth: 0, padding: '14px 12px', textDecoration: 'none', color: 'inherit' }}>
                <div style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 4 }}>Online Customers</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
                  <Users size={18} color="var(--text-muted)" />
                  <span style={{ fontSize: 'clamp(1.5rem, 6vw, 2.2rem)', fontWeight: 800, color: 'var(--text)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                    {bw.onlineCount ?? '—'}
                  </span>
                  {bw.blockedCount > 0 && (
                    <span title="Blocked at Netzur — open the list to see why" style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--c-dc2626)' }}>
                      ({bw.blockedCount} blocked)
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '.68rem', color: 'var(--text-muted)', marginTop: 2 }}>via Netzur, site-wide — click to view list</div>
              </Link>
            </div>
          )}

          <div style={{ padding: '10px 4px 4px' }}>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={history} margin={{ top: 4, right: 14, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="nasUplinkRxG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.2} /><stop offset="95%" stopColor="var(--primary)" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="nasUplinkTxG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--cl-ea580c)" stopOpacity={0.2} /><stop offset="95%" stopColor="var(--cl-ea580c)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--cl-f1f5f9)" />
                <XAxis dataKey="time" tick={{ fontSize: 10 }} minTickGap={40} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `${v}M`} width={44} domain={[0, 'auto']} />
                <Tooltip contentStyle={{ fontSize: '.78rem', borderRadius: 6 }} formatter={(v, n) => [`${v} Mbps`, n === 'rx' ? '↓ Download' : '↑ Upload']} />
                <Legend formatter={n => n === 'rx' ? '↓ Download' : '↑ Upload'} wrapperStyle={{ fontSize: '.78rem', paddingTop: 6 }} />
                <Area type="monotone" dataKey="rx" stroke="var(--primary)" strokeWidth={2} fill="url(#nasUplinkRxG)" dot={false} activeDot={{ r: 3 }} isAnimationActive={false} connectNulls={false} />
                <Area type="monotone" dataKey="tx" stroke="var(--cl-ea580c)" strokeWidth={2} fill="url(#nasUplinkTxG)" dot={false} activeDot={{ r: 3 }} isAnimationActive={false} connectNulls={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </>
      ))}
    </div>
  );
}

// Quick "log a lead from the office" form — dashboard-only entry point for staff (the field
// agent app has its own separate one at agent/AgentLeadPage.js, tied to a different lead
// source/permission). Lands in the same /connection-requests inbox as everything else.
function AddLeadModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({ name: '', mobile: '', society: '', houseNo: '', address: '', connectionType: '', note: '' });
  const [saving, setSaving] = useState(false);
  const [areaOptions, setAreaOptions] = useState([]);

  useEffect(() => { customerAPI.getAreas().then(({ data }) => setAreaOptions(data.data || [])).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim()) { toast.error('Name and mobile are required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, connectionType: form.connectionType || undefined };
      const { data } = await connectionRequestAPI.create(payload);
      toast.success(data.message);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add lead');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h3><UserPlus size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} /> Add Lead</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={submit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Name</label>
              <input className="form-control" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Mobile</label>
              <input className="form-control" value={form.mobile} onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Society / Area (optional)</label>
              <input className="form-control" list="add-lead-area-options" value={form.society}
                onChange={e => setForm(f => ({ ...f, society: e.target.value }))} placeholder="Select or type a society/area" />
              <datalist id="add-lead-area-options">
                {areaOptions.map(a => <option key={a} value={a} />)}
              </datalist>
            </div>
            <div className="form-group">
              <label className="form-label">House No. (optional)</label>
              <input className="form-control" value={form.houseNo} onChange={e => setForm(f => ({ ...f, houseNo: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Interested In (optional)</label>
              <select className="form-control" value={form.connectionType} onChange={e => setForm(f => ({ ...f, connectionType: e.target.value }))}>
                <option value="">Not sure yet</option>
                <option value="broadband">Broadband</option>
                <option value="catv">CableTV</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Address (optional)</label>
              <input className="form-control" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Note (optional)</label>
              <textarea className="form-control" rows={2} value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save Lead'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Dashboard() {
  useDarkReady();
  const { isAdmin } = useAuth();
  // App View (bottom-tab shell) gets its own compact layout — see AppDashboard.js.
  const { viewMode } = useViewMode();
  // Admin-only: last known GTPL wallet per entity (stored after GTPL activity, not read live).
  const [gtplWallets, setGtplWallets] = useState(null);
  const [gtplRefreshing, setGtplRefreshing] = useState(false);
  const refreshGtplWallets = async () => {
    setGtplRefreshing(true);
    try {
      const r = await gtplSaathiAPI.refreshEntityWallets();
      setGtplWallets(r.data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not read GTPL wallets');
    } finally {
      setGtplRefreshing(false);
    }
  };
  useEffect(() => {
    if (isAdmin) gtplSaathiAPI.getEntityWallets().then(r => setGtplWallets(r.data.data)).catch(() => {});
  }, [isAdmin]);
  // Saved GTPL Saathi session: green = saved and last check not failed, red otherwise (null = still loading).
  const [gtplSession, setGtplSession] = useState(null);
  useEffect(() => {
    if (isAdmin) gtplSaathiAPI.getSessionStatus().then(r => setGtplSession(r.data.data)).catch(() => {});
  }, [isAdmin]);
  const [overview, setOverview] = useState(null);
  const [growth, setGrowth] = useState([]);
  const [paySummary, setPaySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leadTotal, setLeadTotal] = useState(null);
  const [addLeadOpen, setAddLeadOpen] = useState(false);

  // stats.total is new+contacted+converted+rejected (deleted leads excluded — see
  // routes/connectionRequests.js GET /stats).
  const loadLeadStats = () => {
    connectionRequestAPI.getStats().then(r => setLeadTotal(r.data.data.new + r.data.data.contacted)).catch(() => {});
  };
  useEffect(() => { loadLeadStats(); }, []);

  // Optional widgets (Settings → App View → Dashboard Widgets), off by default —
  // recentActivity/pollers are only fetched once we know the corresponding widget is on.
  const [widgets, setWidgets] = useState({ showRecentPayments: false, showNasBandwidth: false, showRevenueTrend: false });
  const [activity, setActivity] = useState(null);
  const [pollers, setPollers] = useState([]); // [{ id, name }]
  const [nasOpen, setNasOpen] = useState(false);

  useEffect(() => {
    settingsAPI.getDashboardWidgets().then(r => {
      const w = r.data.data || {};
      setWidgets(w);
      if (w.showRecentPayments) dashboardAPI.getRecentActivity().then(r2 => setActivity(r2.data.data)).catch(() => {});
      if (w.showNasBandwidth) nasAPI.getPollers().then(r2 => setPollers(r2.data.data || [])).catch(() => {});
    }).catch(() => {});
  }, []);

  // Collection tabs: "Today's Collection" (all methods) vs "Monthly Online Collection"
  // (paymentMethod 'payment_gateway' or legacy 'online' — the payment-gateway method,
  // distinct from 'upi').
  const [collTab, setCollTab] = useState('today');
  const [collList, setCollList] = useState([]);
  const [collLoading, setCollLoading] = useState(false);
  const [txnOpen, setTxnOpen] = useState(false);
  const [exportRange, setExportRange] = useState({ from: localDateStr(new Date()), to: localDateStr(new Date()) });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [ov, gr, ps] = await Promise.all([
          dashboardAPI.getOverview(),
          dashboardAPI.getMonthlyGrowth(),
          paymentAPI.getSummary(),
        ]);
        setOverview(ov.data.data);
        setGrowth(gr.data.data);
        setPaySummary(ps.data.data);
      } catch (err) {
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line

  useEffect(() => {
    let cancelled = false;
    const loadCollList = async () => {
      setCollLoading(true);
      try {
        const now = new Date();
        const todayStr = localDateStr(now);
        const monthStartStr = localDateStr(new Date(now.getFullYear(), now.getMonth(), 1));
        const params = collTab === 'today'
          ? { startDate: todayStr, endDate: todayStr, status: 'completed', limit: 10 }
          : { startDate: monthStartStr, method: 'online', status: 'completed', limit: 10 };
        const { data } = await paymentAPI.getAll(params);
        // 'coupon' payments are the internal combo-bill bookkeeping split (see
        // payments/summary's todayMatch comment), not real cash collected today — excluded
        // here to match the total above; not filtered in the shared /payments endpoint
        // itself since other pages (the main Payments list, customer history) need to see
        // these entries. 'company_paid' (AMP auto-paid bills) is excluded the same way — an
        // AMP customer's transaction must never surface on the Dashboard.
        const list = collTab === 'today' ? (data.data || []).filter(p => p.paymentMethod !== 'coupon' && p.paymentMethod !== 'company_paid') : (data.data || []);
        if (!cancelled) setCollList(list);
      } catch {
        if (!cancelled) setCollList([]);
      } finally {
        if (!cancelled) setCollLoading(false);
      }
    };
    loadCollList();
    return () => { cancelled = true; };
  }, [collTab]);

  const handleExport = async () => {
    if (!exportRange.from || !exportRange.to) { toast.error('Pick both a from and to date'); return; }
    setExporting(true);
    try {
      const { data } = await paymentAPI.exportCollections({ startDate: exportRange.from, endDate: exportRange.to });
      const url = URL.createObjectURL(new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `cabletv-collections-${exportRange.from}-to-${exportRange.to}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="loading-screen" style={{ height: 300 }}><div className="spinner" /></div>;
  if (!overview) return null;

  const hov = e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.1)'; };
  const hol = e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; };

  const newTotal = overview.customers.newBroadbandThisMonth + overview.customers.newCatvThisMonth + overview.customers.newComboThisMonth;

  if (viewMode === 'app') {
    return (
      <>
        <AppDashboard
          overview={overview} paySummary={paySummary} newTotal={newTotal} leadTotal={leadTotal} isAdmin={isAdmin}
          gtplWallets={gtplWallets} gtplSession={gtplSession} onRefreshWallets={refreshGtplWallets} walletsRefreshing={gtplRefreshing}
          live={IS_DEMO ? null : <NASUplinkBandwidthPanel />}
        />
        <AssistantFab appMode />
      </>
    );
  }

  return (
    <div>
      {/* Quick actions */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <Link to="/customers/add" className="btn btn-primary"><Users size={15} /> Add Customer</Link>
        <Link to="/renewals" className="btn btn-warning"><RefreshCw size={15} /> View Renewals</Link>
        <Link to="/payments/advance?new=1" className="btn btn-secondary" title="Collect payment before the connection is done and send a proof-of-payment receipt"><Receipt size={15} /> Advance Receipt</Link>
      </div>

      {/* GTPL entity wallets — admin only. Last known balance, updated after GTPL orders. */}
      {isAdmin && gtplWallets && !HIDE_GTPL && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-body" style={{ padding: '10px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontWeight: 600, fontSize: '.85rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                GTPL Wallets
                {gtplSession && (() => {
                  const ok = gtplSession.configured && gtplSession.lastTestResult !== 'failed';
                  return (
                    <span
                      title={ok ? 'GTPL Saathi session is active' : 'GTPL Saathi session is not active — log in again in Settings → GTPL Saathi'}
                      style={{ width: 12, height: 12, borderRadius: '50%', background: ok ? 'var(--success, #16a34a)' : 'var(--danger, #dc2626)' }}
                    />
                  );
                })()}
              </div>
              <button className="btn btn-secondary btn-sm" onClick={refreshGtplWallets} disabled={gtplRefreshing} title="Read the wallets from GTPL now (takes up to 30 seconds)">
                <RefreshCw size={13} style={{ marginRight: 4 }} className={gtplRefreshing ? 'spin' : undefined} />
                {gtplRefreshing ? 'Reading…' : 'Refresh'}
              </button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {gtplWallets.entities.map(e => {
                const low = e.balance != null && e.balance < gtplWallets.lowBalance;
                return (
                  <div key={e.code} style={{ flex: '1 1 140px', padding: '8px 10px', border: `1px solid ${low ? 'var(--danger, #dc2626)' : 'var(--border)'}`, borderRadius: 8 }}>
                    <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{e.code}</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 700, color: low ? 'var(--danger, #dc2626)' : undefined }}>
                      {e.balance != null ? `₹${e.balance.toLocaleString('en-IN', { maximumFractionDigits: 2 })}` : '—'}
                    </div>
                    <div style={{ fontSize: '.68rem', color: 'var(--text-muted)' }}>
                      {e.fetchedAt ? `as of ${new Date(e.fetchedAt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : 'not read yet'}
                      {e.error ? ' · last read failed' : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Stat Cards — compact (stat-card-compact) so the whole page, including the live
          bandwidth graph below, fits in one view without scrolling. */}
      <div className="dashboard-stat-grid" style={{ gap: 10, marginBottom: 14 }}>

        {/* ── Total Customers (bix42-style: big number + label left, icon right; view-only —
            only the type badges below are clickable) ── */}
        <div className="stat-card stat-card-compact dashboard-stat-capped" style={{ display: 'block' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, lineHeight: 1, color: 'var(--text)' }}>{overview.customers.total}</div>
              <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 3 }}>Total Customers</div>
              <div style={{ fontSize: '.64rem', color: 'var(--text-muted)', marginTop: 1 }}>{overview.customers.active} active</div>
            </div>
            <div className="stat-icon" style={{ background: 'var(--cb-dbeafe)', flexShrink: 0 }}><Users size={15} color="var(--primary)" /></div>
          </div>
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {[
              { label: 'Broadband', val: overview.customers.broadband, color: 'var(--c-0891b2)', bg: 'var(--cb-cffafe)', type: 'broadband' },
              { label: 'CableTV',   val: overview.customers.catv,      color: 'var(--primary)', bg: 'var(--cb-ede9fe)', type: 'catv' },
            ].map(t => (
              <Link key={t.label} to={`/customers?customerType=${t.type}`} title={`View ${t.label} customers`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 10, background: t.bg, fontSize: '.68rem', fontWeight: 600, color: t.color, textDecoration: 'none' }}>
                {t.label} <strong style={{ fontSize: '.74rem' }}>{t.val}</strong>
              </Link>
            ))}
            {/* Manually linked pairs (Customer.linkedAccount, via Link CableTV ↔ Broadband)
                — not a customerType, so it's not part of the same map above. */}
            <Link to="/link-customers" title="View combo (linked) CableTV ↔ Broadband accounts"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 10, background: 'var(--cb-e0e7ff)', fontSize: '.68rem', fontWeight: 600, color: 'var(--c-4338ca)', textDecoration: 'none' }}>
              Combo <strong style={{ fontSize: '.74rem' }}>{overview.customers.linkedCombo ?? 0}</strong>
            </Link>
          </div>
        </div>

        {/* ── CableTV Customers (bix42-style; view-only — only the status badges below are
            clickable). Hidden on the demo. ── */}
        {!IS_DEMO && (
          <div className="stat-card stat-card-compact dashboard-stat-capped" style={{ display: 'block' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <div>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, lineHeight: 1, color: 'var(--text)' }}>
                  {overview.customers.catvActive + overview.customers.catvInactive + overview.customers.catvSuspended}
                </div>
                <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 3 }}>CableTV Customers</div>
                <div style={{ fontSize: '.64rem', color: 'var(--text-muted)', marginTop: 1 }}>active + inactive + suspended</div>
              </div>
              <div className="stat-icon" style={{ background: 'var(--cb-ede9fe)', flexShrink: 0 }}><Tv size={15} color="var(--primary)" /></div>
            </div>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Link to="/customers?customerType=catv&status=active" title="View active CableTV customers"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 10, background: 'var(--cb-d1fae5)', fontSize: '.68rem', fontWeight: 600, color: 'var(--c-059669)', textDecoration: 'none' }}>
                Active <strong style={{ fontSize: '.74rem' }}>{overview.customers.catvActive}</strong>
              </Link>
              <Link to="/customers?customerType=catv&status=inactive" title="View inactive CableTV customers"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 10, background: 'var(--cb-fee2e2)', fontSize: '.68rem', fontWeight: 600, color: 'var(--c-dc2626)', textDecoration: 'none' }}>
                Inactive <strong style={{ fontSize: '.74rem' }}>{overview.customers.catvInactive}</strong>
              </Link>
              <Link to="/customers?customerType=catv&status=suspended" title="View suspended CableTV customers"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 10, background: 'var(--cb-fef3c7)', fontSize: '.68rem', fontWeight: 600, color: 'var(--c-d97706)', textDecoration: 'none' }}>
                Suspended <strong style={{ fontSize: '.74rem' }}>{overview.customers.catvSuspended}</strong>
              </Link>
            </div>
          </div>
        )}

        {/* ── New Connections (Month) — bix42-style; view-only, only the type badges below
            are clickable ── */}
        <div className="stat-card stat-card-compact dashboard-stat-capped" style={{ display: 'block' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, lineHeight: 1, color: 'var(--text)' }}>{newTotal}</div>
              <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 3 }}>New Connections</div>
              <div style={{ fontSize: '.64rem', color: 'var(--text-muted)', marginTop: 1 }}>this month</div>
            </div>
            <div className="stat-icon" style={{ background: 'var(--cb-d1fae5)', flexShrink: 0 }}><UserPlus size={15} color="var(--c-059669)" /></div>
          </div>
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 6, display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              { label: 'Broadband', val: overview.customers.newBroadbandThisMonth, color: 'var(--c-0891b2)', bg: 'var(--cb-cffafe)', type: 'broadband' },
              { label: 'CableTV',   val: overview.customers.newCatvThisMonth,      color: 'var(--primary)', bg: 'var(--cb-ede9fe)', type: 'catv' },
            ].map(t => (
              <Link key={t.label} to={`/customers?customerType=${t.type}&newThisMonth=true`} title={`View ${t.label} customers added this month`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 10, background: t.bg, fontSize: '.68rem', fontWeight: 600, color: t.color, textDecoration: 'none' }}>
                {t.label} <strong style={{ fontSize: '.74rem' }}>{t.val}</strong>
              </Link>
            ))}
            {leadTotal !== null && (
              <Link to="/connection-requests" title="View Leads inbox"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 10, background: 'var(--cb-fef3c7)', fontSize: '.68rem', fontWeight: 600, color: 'var(--c-b45309)', textDecoration: 'none' }}>
                Leads <strong style={{ fontSize: '.74rem' }}>{leadTotal}</strong>
              </Link>
            )}
            <button type="button" onClick={() => setAddLeadOpen(true)} title="Add a lead"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 7px', borderRadius: 10, background: 'transparent', border: '1px dashed var(--border)', fontSize: '.68rem', fontWeight: 600, color: 'var(--text-muted)', cursor: 'pointer' }}>
              <Plus size={11} /> Add Lead
            </button>
          </div>
        </div>

        {/* ── This Month Revenue — bix42-style ── */}
        <Link to="/payments" className="stat-card stat-card-compact" style={{ display: 'block', textDecoration: 'none', color: 'inherit', transition: 'transform .12s, box-shadow .12s' }} onMouseEnter={hov} onMouseLeave={hol}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, lineHeight: 1, color: 'var(--text)' }}>₹{fmt(overview.revenue.thisMonth)}</div>
              <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 3 }}>This Month Revenue</div>
              <div className={`trend ${overview.revenue.growth >= 0 ? 'up' : 'down'}`} style={{ fontSize: '.64rem', marginTop: 1 }}>
                {overview.revenue.growth >= 0 ? '+' : ''}{overview.revenue.growth}% vs last month
              </div>
            </div>
            <div className="stat-icon" style={{ background: 'var(--cb-d1fae5)', flexShrink: 0 }}><CreditCard size={15} color="var(--c-059669)" /></div>
          </div>
          <div style={{ marginTop: 8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 10, background: 'var(--cb-dbeafe)', fontSize: '.68rem', fontWeight: 600, color: 'var(--c-2563eb)' }}>
              CableTV <strong style={{ fontSize: '.74rem' }}>₹{fmt(overview.revenue.thisMonthCatv)}</strong>
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 10, background: 'var(--cb-ede9fe)', fontSize: '.68rem', fontWeight: 600, color: 'var(--c-7c3aed)' }}>
              Broadband <strong style={{ fontSize: '.74rem' }}>₹{fmt(overview.revenue.thisMonthBroadband)}</strong>
            </span>
          </div>
        </Link>

        {/* ── Total Pending Amount — same white-card/icon-badge layout as the other cards
            (value red for emphasis), same tile used on the Agent Detail/Collection pages. ── */}
        <Link to="/customers?customerType=catv&status=active&balance=due" className="stat-card stat-card-compact" style={{
          display: 'block', textDecoration: 'none', color: 'inherit', transition: 'transform .12s, box-shadow .12s',
        }} onMouseEnter={hov} onMouseLeave={hol}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--c-b91c1c)', lineHeight: 1 }}>₹{fmt(overview.outstanding)}</div>
              <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 3 }}>Total Pending Amount</div>
            </div>
            <div className="stat-icon" style={{ background: 'var(--cb-fee2e2)', flexShrink: 0 }}><IndianRupee size={15} color="var(--c-dc2626)" /></div>
          </div>
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 6 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 10, background: 'var(--cb-fee2e2)', fontSize: '.68rem', fontWeight: 600, color: 'var(--c-dc2626)' }}>
              From <strong style={{ fontSize: '.74rem' }}>{overview.outstandingCustomers}</strong> Customers
            </span>
          </div>
        </Link>

        {/* ── Expiry (bix42-style: label left, icon right) ── */}
        <Link to="/renewals" className="stat-card stat-card-compact" style={{ textDecoration: 'none', color: 'inherit', display: 'block', transition: 'transform .12s, box-shadow .12s' }} onMouseEnter={hov} onMouseLeave={hol}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: '.68rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Expiry</div>
            <div className="stat-icon" style={{ background: 'var(--cb-fef3c7)', flexShrink: 0 }}><Clock size={15} color="var(--c-d97706)" /></div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {[
              { label: 'Today',    val: overview.renewals.today,    dot: 'var(--cb-f97316)', textColor: 'var(--c-ea580c)' },
              { label: 'Tomorrow', val: overview.renewals.tomorrow, dot: 'var(--cb-eab308)', textColor: 'var(--c-a16207)' },
              { label: 'Expired',  val: overview.renewals.expired,  dot: 'var(--cb-ef4444)', textColor: 'var(--c-dc2626)' },
            ].map(r => (
              <div key={r.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.72rem', color: 'var(--text-muted)' }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: r.dot, display: 'inline-block', flexShrink: 0 }} />
                  {r.label}
                </span>
                <span style={{ fontWeight: 800, fontSize: '.9rem', color: r.textColor }}>{r.val}</span>
              </div>
            ))}
          </div>
        </Link>

        {/* ── Open Complaints — bix42-style ── */}
        <Link to="/complaints" className="stat-card stat-card-compact" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', textDecoration: 'none', color: 'inherit', transition: 'transform .12s, box-shadow .12s' }} onMouseEnter={hov} onMouseLeave={hol}>
          <div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, lineHeight: 1, color: 'var(--text)' }}>{overview.complaints.open}</div>
            <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 3 }}>Open Complaints</div>
            <div style={{ fontSize: '.64rem', color: 'var(--text-muted)', marginTop: 1 }}>{overview.complaints.critical} critical</div>
          </div>
          <div className="stat-icon" style={{ background: 'var(--cb-fee2e2)', flexShrink: 0 }}><MessageSquare size={15} color="var(--c-dc2626)" /></div>
        </Link>

      </div>

      {/* NAS Uplink Bandwidth — Ellora↔CTPL backhaul, combined from both NAS points. The
          dashboard's top live-bandwidth widget. Separate section from the per-NAS detail
          panels below (those cover each NAS's own full WAN interface set; this one is
          specifically the shared corridor between them). */}
      {!IS_DEMO && <NASUplinkBandwidthPanel />}

      {/* NAS Bandwidth — opt-in (Settings → App View → Dashboard Widgets), one panel per
          active NAS poller, click-to-collapse same as the "ACS Connection Details" panel
          on the TR-069 page (AcsPage.js ConnectionInfoPanel) */}
      {widgets.showNasBandwidth && (
        <>
          <div className="card" style={{ marginBottom: 10 }}>
            <div
              className="card-header"
              style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
              onClick={() => setNasOpen(o => !o)}
            >
              <Activity size={15} color="var(--primary)" />
              <h3 style={{ margin: 0, fontSize: '.95rem', flex: 1 }}>NAS Bandwidth</h3>
              {nasOpen ? <ChevronUp size={16} color="var(--text-muted)" /> : <ChevronDown size={16} color="var(--text-muted)" />}
            </div>
          </div>
          {nasOpen && (
            pollers.length === 0 ? (
              <div className="card mb-6" style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)', fontSize: '.85rem' }}>
                <Activity size={26} style={{ opacity: .3, marginBottom: 8 }} />
                <div>No NAS configured. Add one in Settings → NAS Devices.</div>
              </div>
            ) : (
              pollers.map(p => <NASBandwidthPanel key={p.id} nasId={p.id} nasName={p.name} />)
            )
          )}
        </>
      )}

      {/* Today's collection + recent activity — moved above the charts row so the
          collection numbers are the first thing seen after the stat cards. */}
      <div className="mb-6">
        <div className="card">
          <div className="card-header" style={{ flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', gap: 6 }}>
              {[
                { key: 'today', label: "Today's Collection" },
                { key: 'onlineMonth', label: 'Monthly Online Collection' },
              ].map(t => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setCollTab(t.key)}
                  className={`btn btn-sm ${collTab === t.key ? 'btn-primary' : 'btn-outline'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <Link to="/payments" className="btn btn-outline btn-sm">View All</Link>
          </div>
          <div className="card-body">
            <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--success)', marginBottom: 4 }}>
              ₹{fmt(collTab === 'today' ? (paySummary?.today?.amount || 0) : (paySummary?.onlineMonth?.amount || 0))}
            </div>
            <p style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginBottom: 16 }}>
              {collTab === 'today'
                ? `${paySummary?.today?.count || 0} transactions today`
                : `${paySummary?.onlineMonth?.count || 0} payment-gateway transactions this month`}
            </p>

            {/* Split by service — what was collected against Broadband vs CableTV. Every
                payment lands in one of the two (a non-renewal payment — installation,
                security deposit, generic gateway balance top-up — is allocated to whichever
                service the customer actually has, see payments.js /summary). Shown for both
                tabs: a gateway ("Monthly Online Collection") payment is still either a
                Broadband or CableTV renewal, same as a Today's Collection one. */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1, background: 'var(--bg)', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2 }}>Broadband</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 800 }}>₹{fmt(paySummary?.[collTab]?.broadband || 0)}</div>
              </div>
              <div style={{ flex: 1, background: 'var(--bg)', borderRadius: 8, padding: '10px 14px' }}>
                <div style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2 }}>CableTV</div>
                <div style={{ fontSize: '1.2rem', fontWeight: 800 }}>₹{fmt(paySummary?.[collTab]?.catv || 0)}</div>
              </div>
            </div>

            {collTab === 'today' && (
              <>
                {/* Split by how it was paid — cash is the figure that has to be physically
                    counted and deposited, so it gets its own tile rather than sitting in the
                    method list below. "Non-cash" is everything else (UPI, gateway, cheque,
                    bank transfer, and any custom method). Online-only tab has no cash concept
                    by definition, so this stays today-only. */}
                <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                  <div style={{ flex: 1, background: 'var(--cb-f0fdf4)', border: '1px solid var(--cb-bbf7d0)', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--c-15803d)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 5 }}>
                      <Banknote size={13} /> Cash
                    </div>
                    <div style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--c-15803d)' }}>₹{fmt(paySummary?.today?.cash || 0)}</div>
                    <div style={{ fontSize: '.68rem', color: 'var(--text-muted)', marginTop: 1 }}>
                      {paySummary?.today?.cashCount || 0} of {paySummary?.today?.count || 0} payments
                    </div>
                  </div>
                  <div style={{ flex: 1, background: 'var(--bg)', borderRadius: 8, padding: '10px 14px' }}>
                    <div style={{ fontSize: '.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2 }}>Non-Cash</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: 800 }}>
                      ₹{fmt(Math.max(0, (paySummary?.today?.amount || 0) - (paySummary?.today?.cash || 0)))}
                    </div>
                    <div style={{ fontSize: '.68rem', color: 'var(--text-muted)', marginTop: 1 }}>UPI, gateway, cheque, transfer</div>
                  </div>
                </div>
              </>
            )}

            {collTab === 'today' && paySummary?.methodBreakdown?.map(m => (
              <div key={m._id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '1px solid var(--border)', fontSize: '.85rem' }}>
                <span style={{ textTransform: 'capitalize' }}>{m._id?.replace('_', ' ')}</span>
                <span style={{ fontWeight: 600 }}>₹{fmt(m.total)}</span>
              </div>
            ))}

            {/* Separate section: the actual transactions behind whichever tab is active —
                collapsed by default, click to expand only when the customer-level detail
                is actually needed. */}
            <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
              <button
                type="button"
                onClick={() => setTxnOpen(o => !o)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '.82rem', color: 'var(--text)', fontWeight: 600 }}
              >
                Transactions {collLoading ? '' : `(${collList.length})`}
                {txnOpen ? <ChevronUp size={16} color="var(--text-muted)" /> : <ChevronDown size={16} color="var(--text-muted)" />}
              </button>
              {txnOpen && (
                collLoading ? (
                  <div style={{ textAlign: 'center', padding: '12px 0', color: 'var(--text-muted)', fontSize: '.82rem' }}>Loading…</div>
                ) : collList.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '12px 0', color: 'var(--text-muted)', fontSize: '.82rem' }}>No transactions</div>
                ) : (
                  collList.map(p => (
                    <div key={p._id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderTop: '1px solid var(--border)', fontSize: '.82rem' }}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{p.customer?.name || '—'}</div>
                        <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>
                          {p.customer?.customerId} · {new Date(p.paymentDate).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </div>
                        {(() => {
                          // Society = address.area; house no. follows the same customerType
                          // convention as payments.js's serviceType filter — 'both' counts as catv.
                          const houseNo = p.customer?.customerType === 'broadband'
                            ? p.customer?.broadband?.houseNumber
                            : p.customer?.catv?.houseNumber;
                          const societyLine = [p.customer?.address?.area, houseNo].filter(Boolean).join(' - ');
                          return societyLine ? (
                            <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{societyLine}</div>
                          ) : null;
                        })()}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontWeight: 600, color: 'var(--success)' }}>₹{fmt(p.totalAmount)}</div>
                        {collTab === 'today' && (
                          <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{p.paymentMethod?.replace('_', ' ')}</div>
                        )}
                      </div>
                    </div>
                  ))
                )
              )}
            </div>

            {/* Separate section: Excel export over an arbitrary date range */}
            <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
              <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.04em' }}>
                Export Collections (CableTV only)
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  type="date" className="form-control" style={{ width: 145 }}
                  value={exportRange.from}
                  onChange={e => setExportRange(r => ({ ...r, from: e.target.value }))}
                />
                <span style={{ color: 'var(--text-muted)', fontSize: '.8rem' }}>to</span>
                <input
                  type="date" className="form-control" style={{ width: 145 }}
                  value={exportRange.to}
                  onChange={e => setExportRange(r => ({ ...r, to: e.target.value }))}
                />
                <button type="button" className="btn btn-primary btn-sm" onClick={handleExport} disabled={exporting}>
                  {exporting ? 'Exporting…' : 'Export Excel'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts row */}
      <div className={widgets.showRevenueTrend ? 'dashboard-grid-2 mb-6' : 'mb-6'}>
        {/* Revenue trend — opt-in (Settings → App View → Dashboard Widgets) */}
        {widgets.showRevenueTrend && (
          <div className="card">
            <div className="card-header">
              <h3>Revenue Trend (6 Months)</h3>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={growth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--cl-f1f5f9)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={v => [`₹${fmt(v)}`, 'Revenue']} />
                  <Line type="monotone" dataKey="revenue" stroke="var(--primary)" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* New customers trend */}
        <div className="card">
          <div className="card-header">
            <h3>New Customers (6 Months)</h3>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={growth}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--cl-f1f5f9)" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="newCustomers" fill="var(--accent)" radius={[4, 4, 0, 0]} name="New Customers" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent Payments — opt-in (Settings → App View → Dashboard Widgets) */}
      {widgets.showRecentPayments && (
        <div className="card mb-6">
          <div className="card-header">
            <h3>Recent Payments</h3>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Method</th>
                </tr>
              </thead>
              <tbody>
                {activity?.recentPayments?.map(p => {
                  const typeMap = { catv: 'CableTV', broadband: 'Broadband', both: 'Both' };
                  const typeLabel = typeMap[p.customer?.customerType] || '-';
                  const typeBadge = `badge-${p.customer?.customerType || 'info'}`;
                  return (
                    <tr key={p._id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{p.customer?.name}</div>
                        <div style={{ fontSize: '.75rem', color: 'var(--text-muted)' }}>{p.customer?.customerId}</div>
                      </td>
                      <td><span className={`badge ${typeBadge}`}>{typeLabel}</span></td>
                      <td style={{ fontWeight: 600, color: 'var(--success)' }}>₹{fmt(p.totalAmount)}</td>
                      <td><span className="badge badge-info" style={{ textTransform: 'capitalize' }}>{p.paymentMethod}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {addLeadOpen && (
        <AddLeadModal onClose={() => setAddLeadOpen(false)} onSuccess={() => { setAddLeadOpen(false); loadLeadStats(); }} />
      )}
      <AssistantFab />
    </div>
  );
}
